/**
 * Prevent MAX mini-app sheet from closing when the user scrolls long pages.
 * Official Bridge expands on WebAppReady but still rubber-bands the host sheet
 * if document/body scrolls. We lock the document and keep scroll in an inner pane.
 */

function isVerticallyScrollable(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  const oy = style.overflowY;
  if (oy !== "auto" && oy !== "scroll" && oy !== "overlay") return false;
  return el.scrollHeight > el.clientHeight + 1;
}

function findScrollableAncestor(start: EventTarget | null): HTMLElement | null {
  let el = start instanceof Element ? (start as HTMLElement) : null;
  while (el && el !== document.documentElement) {
    if (isVerticallyScrollable(el)) return el;
    el = el.parentElement;
  }
  return null;
}

let touchGuardInstalled = false;
let lastTouchY = 0;

function onTouchStart(e: TouchEvent) {
  if (e.touches.length === 1) lastTouchY = e.touches[0]!.clientY;
}

function onTouchMove(e: TouchEvent) {
  if (e.touches.length !== 1) return;
  const target = e.target as HTMLElement | null;
  // Map pans horizontally/vertically inside the WebView — never let the host sheet
  // steal the gesture (MAX may close the mini-app on edge / rubber-band swipe).
  if (target?.closest?.("[data-max-map-pan]")) {
    e.preventDefault();
    return;
  }
  // Explicit opt-out for rare cases that need native overscroll.
  if (target?.closest?.("[data-max-allow-overscroll]")) return;

  const scrollEl = findScrollableAncestor(e.target);
  if (!scrollEl) {
    // No inner scroller → block document rubber-band (closes the mini-app).
    e.preventDefault();
    return;
  }

  const dy = e.touches[0]!.clientY - lastTouchY;
  lastTouchY = e.touches[0]!.clientY;
  const atTop = scrollEl.scrollTop <= 0;
  const atBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 1;

  // Block overscroll past edges so the host sheet does not swallow the gesture.
  if ((atTop && dy > 0) || (atBottom && dy < 0)) {
    e.preventDefault();
  }
}

export function installMaxScrollGuard() {
  if (typeof document === "undefined" || touchGuardInstalled) return;
  touchGuardInstalled = true;
  document.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
  document.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
}

export function applyMaxDocumentLock() {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  const body = document.body;
  html.classList.add("max-webapp");
  body?.classList.add("max-webapp");
  html.style.overflow = "hidden";
  if (body) {
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.left = "0";
    body.style.right = "0";
    body.style.top = body.style.top || "0";
    body.style.width = "100%";
    body.style.height = "var(--max-viewport-height, 100%)";
    body.style.touchAction = "manipulation";
  }
  installMaxScrollGuard();
}
